import React, { useState, useCallback, useEffect, useMemo } from "react";
import DataMappingDropdownGroup from "./DataMappingDropdownGroup";
import { useParams } from "react-router-dom";
import { CsrAPI } from "../apis/CsrAPI";
import { toast } from "react-toastify";
import { CButton, CSpinner } from "@coreui/react";

const DataMappingContent = ({ selections, setSelections, sharedTableData }) => {
  const { studyId } = useParams();
  // Ensure selections has default values if undefined/null
  const defaultSafeSelections = selections || {
    demography: [],
    diseaseCharacteristics: [],
    death: [],
    discontinuation: [],
    frequency: [],
    otherSignificant: [],
    safetySummary: [],
    serious: [],
    severity: [],
    specialInterest: [],
    treatmentRelated: [],
    protocolDeviations: [],
    primaryEfficacy: [],
    secondaryEfficacy: [],
    dsHematology: [],
    avHematology: [],
    stHematology: [],
    dsSerumChemistry: [],
    avSerumChemistry: [],
    stSerumChemistry: [],
    dsUrinalysis: [],
    avUrinalysis: [],
    stUrinalysis: [],
    dsSpecialInterest: [],
    avSpecialInterest: [],
    stSpecialInterest: [],
    dsVitalSigns: [],
    avVitalSigns: [],
    stVitalSigns: [],
    dsEcg: [],
    avEcg: [],
    stEcg: [],
    medicalHistory: [],
    diseaseMedicalHistory: [],
    concomitantTherapy: [],
    diseaseConcomitantTherapy: [],
    exposure: [],
    compliance: [],
    diseaseCompliance: [],
  };

  const [safeSelections, setSafeSelections] = useState(defaultSafeSelections);
  const [hasLoadedSavedData, setHasLoadedSavedData] = useState(false);
  const [isAutoMapping, setIsAutoMapping] = useState(false);

  // Create a stable reference for sharedTableData that changes when data changes
  const stableSharedTableData = useMemo(
    () => ({
      ...sharedTableData,
      tableOptionsLength: sharedTableData.tableOptions?.length || 0,
    }),
    [
      sharedTableData.tableOptions,
      sharedTableData.isDataReady,
      sharedTableData.isLoadingData,
      sharedTableData.dataError,
    ]
  );

  // Sync safeSelections with parent selections when they change
  useEffect(() => {
    if (selections && Object.keys(selections).length > 0) {
      // Check if we actually have meaningful data (not just empty arrays)
      const hasMeaningfulData = Object.values(selections).some(
        selection => Array.isArray(selection) && selection.length > 0
      );

      if (hasMeaningfulData) {
        setSafeSelections(selections);
        setHasLoadedSavedData(true);
      } else {
        setSafeSelections(selections);
      }
    }
  }, [selections]);

  // Initialize safeSelections when component mounts if no selections provided
  useEffect(() => {
    if (
      !hasLoadedSavedData &&
      (!selections || Object.keys(selections).length === 0)
    ) {
      setSafeSelections(defaultSafeSelections);
    }
  }, [hasLoadedSavedData, selections]);

  // Helper function to filter options based on input value
  const filterOptions = (options, inputValue) => {
    if (!inputValue) {
      return options;
    }

    return options.filter(option =>
      option.label.toLowerCase().includes(inputValue.toLowerCase())
    );
  };

  // Load options asynchronously using shared data
  const loadOptions = useCallback(
    async inputValue => {
      if (!studyId) {
        return [];
      }

      try {
        // Use shared table data instead of making API calls

        const availableOptions = sharedTableData.tableOptions || [];
        const filteredOptions = filterOptions(availableOptions, inputValue);
        return filteredOptions;
      } catch (err) {
        console.error("Error filtering table options:", err);
        return [];
      }
    },
    [studyId, stableSharedTableData]
  );

  // Update selections for a specific dropdown
  const updateSelections = (dropdownKey, values) => {
    // Update both local state and parent state
    setSafeSelections(prev => ({
      ...prev,
      [dropdownKey]: values,
    }));

    setSelections(prev => ({
      ...prev,
      [dropdownKey]: values,
    }));
  };

  // Handle Map Tables button click
  const handleMapTablesClick = async () => {
    if (!studyId) {
      toast.error("Study ID not found");
      return;
    }

    setIsAutoMapping(true);
    try {
      // Call the PUT /studies/{study_id}/filter endpoint with empty body
      const filterResponse = await CsrAPI.filterTables({ studyId });

      // Use the response to update selections (override GET /questions response)
      if (filterResponse && filterResponse.dataMapping) {
        const newSelections = {
          demography: filterResponse.dataMapping.demography || [],
          diseaseCharacteristics:
            filterResponse.dataMapping.diseaseCharacteristics || [],
          death: filterResponse.dataMapping.death || [],
          discontinuation: filterResponse.dataMapping.discontinuation || [],
          frequency: filterResponse.dataMapping.frequency || [],
          otherSignificant: filterResponse.dataMapping.otherSignificant || [],
          safetySummary: filterResponse.dataMapping.safetySummary || [],
          serious: filterResponse.dataMapping.serious || [],
          severity: filterResponse.dataMapping.severity || [],
          specialInterest: filterResponse.dataMapping.specialInterest || [],
          treatmentRelated: filterResponse.dataMapping.treatmentRelated || [],
          protocolDeviations:
            filterResponse.dataMapping.protocolDeviations || [],
          primaryEfficacy: filterResponse.dataMapping.primaryEfficacy || [],
          secondaryEfficacy: filterResponse.dataMapping.secondaryEfficacy || [],
          dsHematology: filterResponse.dataMapping.dsHematology || [],
          avHematology: filterResponse.dataMapping.avHematology || [],
          stHematology: filterResponse.dataMapping.stHematology || [],
          dsSerumChemistry: filterResponse.dataMapping.dsSerumChemistry || [],
          avSerumChemistry: filterResponse.dataMapping.avSerumChemistry || [],
          stSerumChemistry: filterResponse.dataMapping.stSerumChemistry || [],
          dsUrinalysis: filterResponse.dataMapping.dsUrinalysis || [],
          stUrinalysis: filterResponse.dataMapping.stUrinalysis || [],
          stSpecialInterest: filterResponse.dataMapping.stSpecialInterest || [],
          dsVitalSigns: filterResponse.dataMapping.dsVitalSigns || [],
          avVitalSigns: filterResponse.dataMapping.avVitalSigns || [],
          stVitalSigns: filterResponse.dataMapping.stVitalSigns || [],
          dsEcg: filterResponse.dataMapping.dsEcg || [],
          avEcg: filterResponse.dataMapping.avEcg || [],
          stEcg: filterResponse.dataMapping.stEcg || [],
          medicalHistory: filterResponse.dataMapping.medicalHistory || [],
          diseaseMedicalHistory:
            filterResponse.dataMapping.diseaseMedicalHistory || [],
          concomitantTherapy:
            filterResponse.dataMapping.concomitantTherapy || [],
          diseaseConcomitantTherapy:
            filterResponse.dataMapping.diseaseConcomitantTherapy || [],
          exposure: filterResponse.dataMapping.exposure || [],
          compliance: filterResponse.dataMapping.compliance || [],
          diseaseCompliance: filterResponse.dataMapping.diseaseCompliance || [],
        };

        // Update both local and parent state
        setSafeSelections(newSelections);
        setSelections(newSelections);

        toast.success("Table mapping updated successfully!");
      } else {
        toast.info("No mapping data received from filter endpoint");
      }
    } catch (error) {
      console.error("Error calling filter endpoint:", error);
      toast.error("Failed to map tables. Please try again.");
    } finally {
      setIsAutoMapping(false);
    }
  };

  const dataMappingDropdownList = [
    {
      sectionTitle: "Protocol Deviations",
      childrens: [
        {
          label: "",
          key: "protocolDeviations",
          placeholder: "Select Protocol Deviations data tables...",
          isMulti: true,
        },
      ],
    },
    {
      sectionTitle: "Demographics",
      childrens: [
        {
          label: "Demography",
          key: "demography",
          placeholder: "Select Demography data tables...",
          isMulti: true,
        },
        {
          label: "Baseline Disease Characteristics",
          key: "diseaseCharacteristics",
          placeholder: "Select Baseline Disease Characteristics data tables...",
          isMulti: true,
        },
      ],
    },
    {
      sectionTitle: "Medical History",
      childrens: [
        {
          label: "Medical History",
          key: "medicalHistory",
          placeholder: "Select Medical History data tables...",
          isMulti: true,
        },
        {
          label: "Disease-specific Medical History",
          key: "diseaseMedicalHistory",
          placeholder: "Select Disease-specific Medical History data tables...",
          isMulti: true,
        },
      ],
    },
    {
      sectionTitle: "Prior and Concomitant Therapy",
      childrens: [
        {
          label: "Prior and Concomitant Therapy",
          key: "concomitantTherapy",
          placeholder: "Select Prior and Concomitant Therapy data tables...",
          isMulti: true,
        },
        {
          label: "Disease-specific Prior and Concomitant Therapy",
          key: "diseaseConcomitantTherapy",
          placeholder:
            "Select Disease-specific Prior and Concomitant Therapy data tables...",
          isMulti: true,
        },
      ],
    },
    {
      sectionTitle: "Exposure",
      childrens: [
        {
          label: "",
          key: "exposure",
          placeholder: "Select Exposure data tables...",
          isMulti: true,
        },
      ],
    },
    {
      sectionTitle: "Compliance",
      childrens: [
        {
          label: "Compliance",
          key: "compliance",
          placeholder: "Select Compliance data tables...",
          isMulti: true,
        },
        {
          label: "Disease-specific Compliance",
          key: "diseaseCompliance",
          placeholder: "Select Disease-specific Compliance data tables...",
          isMulti: true,
        },
      ],
    },
    // TODO: Commenting out Efficacy section for now as per Sofia and Gasper's request
    // Will be added back in future iterations

    // {
    //   sectionTitle: "Efficacy",
    //   childrens: [
    //     {
    //       label: "Primary Efficacy",
    //       key: "primaryEfficacy",
    //       placeholder: "Select Primary Efficacy data tables...",
    //       isMulti: true,
    //     },
    //     {
    //       label: "Secondary Efficacy",
    //       key: "secondaryEfficacy",
    //       placeholder: "Select Secondary Efficacy data tables...",
    //       isMulti: true,
    //     },
    //   ],
    // },
    {
      sectionTitle: "Safety",
      childrens: [
        {
          label: "Safety Summary",
          key: "safetySummary",
          placeholder: "Select Safety Summary data tables...",
          isMulti: true,
        },
        {
          label: "Frequency of Adverse Events",
          key: "frequency",
          placeholder: "Select Frequency of Adverse Events data tables...",
          isMulti: true,
        },
        {
          label: "Adverse Events by Severity",
          key: "severity",
          placeholder: "Select Adverse Events by Severity data tables...",
          isMulti: true,
        },
        {
          label: "Treatment-related Adverse Events",
          key: "treatmentRelated",
          placeholder: "Select Treatment-related Adverse Events data tables...",
          isMulti: true,
        },
        {
          label: "Death",
          key: "death",
          placeholder: "Select Death data tables...",
          isMulti: true,
        },
        {
          label: "Serious Adverse Events",
          key: "serious",
          placeholder: "Select Serious Adverse Events data tables...",
          isMulti: true,
        },
        {
          label:
            "Discontinuations and/or Dose Modifications Due to Adverse Events",
          key: "discontinuation",
          placeholder:
            "Select Discontinuations and/or Dose Modifications Due to Adverse Events data tables...",
          isMulti: true,
        },
        {
          label: "Adverse Events of Special Interest",
          key: "specialInterest",
          placeholder:
            "Select Adverse Events of Special Interest data tables...",
          isMulti: true,
        },
        {
          label: "Other Significant Adverse Events",
          key: "otherSignificant",
          placeholder: "Select Other Significant Adverse Events data tables...",
          isMulti: true,
        },
      ],
    },
    {
      sectionTitle: "Clinical Laboratories",
      childrens: [
        {
          label: "Hematology - Descriptive Statistics (Population Changes)",
          key: "dsHematology",
          placeholder:
            "Select hematology - descriptive statistics data tables...",
          isMulti: true,
        },
        {
          label:
            "Hematology - Clinically Meaningful Abnormal Values (Changes for Individuals)",
          key: "avHematology",
          placeholder:
            "Select hematology - clinically meaningful abnormal values data tables...",
          isMulti: true,
        },
        {
          label: "Hematology - Clinically Meaningful Range Changes",
          key: "stHematology",
          placeholder:
            "Select Hematology - Clinically Meaningful Range Changes data tables...",
          isMulti: true,
        },
        {
          label:
            "Serum Chemistry - Descriptive Statistics (Population Changes)",
          key: "dsSerumChemistry",
          placeholder:
            "Select Serum Chemistry - descriptive statistics values data tables...",
          isMulti: true,
        },
        {
          label:
            "Serum Chemistry - Clinically Meaningful Abnormal Values (Changes for Individuals)",
          key: "avSerumChemistry",
          placeholder:
            "Select Serum Chemistry - clinically meaningful abnormal values data tables...",
          isMulti: true,
        },
        {
          label: "Serum Chemistry - Clinically Meaningful Range Changes",
          key: "stSerumChemistry",
          placeholder:
            "Select Serum Chemistry - Clinically Meaningful Range Changes data tables...",
          isMulti: true,
        },
        {
          label: "Urinalysis - Descriptive Statistics (Population Changes)",
          key: "dsUrinalysis",
          placeholder:
            "Select Urinalysis - descriptive statistics values data tables...",
          isMulti: true,
        },
        {
          label:
            "Urinalysis - Clinically Meaningful Abnormal Values (Changes for Individuals)",
          key: "avUrinalysis",
          placeholder:
            "Select Urinalysis - clinically meaningful abnormal values data tables...",
          isMulti: true,
        },
        {
          label: "Urinalysis - Clinically Meaningful Range Changes",
          key: "stUrinalysis",
          placeholder:
            "Select Urinalysis - Clinically Meaningful Range Changes data tables...",
          isMulti: true,
        },
        {
          label:
            "Special Interest Laboratories - Descriptive Statistics (Population Changes)",
          key: "dsSpecialInterest",
          placeholder:
            "Select Special Interest Laboratories - Descriptive Statistics (Population Changes) data tables...",
          isMulti: true,
        },
        {
          label:
            "Special Interest Laboratories - Clinically Meaningful Abnormal Values (Changes for Individuals)",
          key: "avSpecialInterest",
          placeholder:
            "Select Special Interest Laboratories - Clinically Meaningful Abnormal Values (Changes for Individuals) data tables...",
          isMulti: true,
        },
        {
          label:
            "Special Interest Laboratories - Clinically Meaningful Range Changes",
          key: "stSpecialInterest",
          placeholder:
            "Select Special Interest Laboratories - Clinically Meaningful Range Changes data tables...",
          isMulti: true,
        },
        {
          label: "Vital Signs - Descriptive Statistics (Population Changes)",
          key: "dsVitalSigns",
          placeholder:
            "Select Vital Signs - Descriptive Statistics (Population Changes) data tables...",
          isMulti: true,
        },
        {
          label:
            "Vital Signs - Clinically Meaningful Abnormal Values (Changes for Individuals)",
          key: "avVitalSigns",
          placeholder:
            "Select Vital Signs - Clinically Meaningful Abnormal Values (Changes for Individuals) data tables...",
          isMulti: true,
        },
        {
          label: "Vital Signs - Clinically Meaningful Range Changes",
          key: "stVitalSigns",
          placeholder:
            "Select Vital Signs - Clinically Meaningful Range Changes data tables...",
          isMulti: true,
        },
        {
          label:
            "Electrocardiogram - Descriptive Statistics (Population Changes)",
          key: "dsEcg",
          placeholder:
            "Select Electrocardiogram - Descriptive Statistics (Population Changes) data tables...",
          isMulti: true,
        },
        {
          label:
            "Electrocardiogram - Clinically Meaningful Abnormal Values (Changes for Individuals)",
          key: "avEcg",
          placeholder:
            "Select Electrocardiogram - Clinically Meaningful Abnormal Values (Changes for Individuals) data tables...",
          isMulti: true,
        },
        {
          label: "Electrocardiogram - Clinically Meaningful Range Changes",
          key: "stEcg",
          placeholder:
            "Select Electrocardiogram - Clinically Meaningful Range Changes data tables...",
          isMulti: true,
        },
      ],
    },
  ];

  return (
    <div className="data-mapping-content">
      {/* Shared Data Loading State — shown while the backend is still
          parsing the uploaded documents (GET /studies/{id}/status returns
          study_files: false) and until the table names have arrived. */}
      {stableSharedTableData.isLoadingData && (
        <div
          className="questions-content__item"
          data-testid="data-mapping-loader"
          role="status"
          aria-live="polite">
          <div className="questions-content__label intro-text">
            <CSpinner size="sm" className="me-2" />
            <span className="questions-content__label-text">
              Loading table data... We are still processing your uploaded
              documents.
            </span>
          </div>
        </div>
      )}

      {/* Shared Data Error State */}
      {stableSharedTableData.dataError &&
        !stableSharedTableData.isLoadingData && (
          <div className="questions-content__item">
            <div className="questions-content__label intro-text error">
              <span className="questions-content__label-text">
                {stableSharedTableData.dataError}
              </span>
            </div>
          </div>
        )}

      <div className="questions-content__item">
        <div className="questions-content__label intro-text">
          <span className="questions-content__label-text">
            Select tables to map, or
          </span>

          <CButton
            className="input-submit input-submit__primary input-submit__arrow"
            disabled={
              isAutoMapping ||
              !stableSharedTableData.tableOptions ||
              stableSharedTableData.tableOptionsLength === 0
            }
            onClick={e => {
              e.preventDefault();
              handleMapTablesClick();
            }}>
            {isAutoMapping ? (
              <>
                <CSpinner size="sm" className="me-2" />
                Auto-Mapping...
              </>
            ) : (
              "Auto-Map with TriloDocs"
            )}
          </CButton>
        </div>
      </div>

      <div className="data-mapping-sections">
        {/* Render all dropdown groups from configuration */}
        {dataMappingDropdownList.map(section => (
          <DataMappingDropdownGroup
            key={section.sectionTitle}
            section={section}
            safeSelections={safeSelections}
            updateSelections={updateSelections}
            loadOptions={loadOptions}
            stableSharedTableData={stableSharedTableData}
          />
        ))}
      </div>
    </div>
  );
};

export default DataMappingContent;
